import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientError } from '@ricoh/ria.client.core/errors';
import { isNull } from '@ricoh/ria.client.core/utils/data/object';
import { z } from 'zod';

import type { FieldMetadataConditionDescriptor } from '~/common/model/api/batch';

import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';

import type { FieldValidValueSchemaCreator, FieldValueErrorMessages, FieldValueSchemaCreator } from './_value.type';

import { emptySchema } from './_empty';

/**
 * Use Field Value Schema Creator.
 *
 * @returns {FieldValueSchemaCreator} Field Value Schema Creator.
 */
export function useFieldValueSchemaCreator(): FieldValueSchemaCreator {
  const { t } = useTranslation();

  const createErrorMessage = useCallback<Func<[fieldName: string], FieldValueErrorMessages>>(
    (fieldName) => ({
      invalid_type_error: t('validation:invalidType', { fieldName }),
      required_error: t('validation:required', { fieldName }),
      message: t('validation:invalid', { fieldName }),
    }),
    [t],
  );

  const createErrorMap = useCallback<Func<[hasNotNullCondition: boolean, messages: FieldValueErrorMessages], z.ZodErrorMap>>(
    (hasNotNullCondition, messages) =>
      (issue, { data }) => {
        if (hasNotNullCondition && issue.code === 'invalid_type' && isNull(data)) {
          return { message: messages.required_error };
        }

        return { message: messages.invalid_type_error };
      },
    [],
  );

  const createTableSchema = useCallback<Func<[hasNotNullCondition: boolean, messages: FieldValueErrorMessages, errorMap: z.ZodErrorMap], z.ZodTypeAny>>(
    (hasNotNullCondition, messages, errorMap) => {
      const schema = z.array(z.object({}, { errorMap }), { errorMap });

      if (hasNotNullCondition) {
        return schema.nonempty(messages.required_error);
      }

      return z.nullable(schema);
    },
    [],
  );

  const createTextSchema = useCallback<
    Func<[fieldName: string, conditions: FieldMetadataConditionDescriptor[], messages: FieldValueErrorMessages, errorMap: z.ZodErrorMap], z.ZodTypeAny>
  >(
    (fieldName, conditions, messages, errorMap) => {
      let schema = z.string({ errorMap });

      const notEmpty = conditions.find(({ operator }) => operator === 'notEmpty');
      if (notEmpty) {
        schema = schema.min(1, {
          message: messages.required_error,
        });
      }

      const greaterThanOrEqualTo = conditions.find(({ operator }) => operator === 'greaterThanOrEquals');
      if (greaterThanOrEqualTo && typeof greaterThanOrEqualTo.value?.value === 'number') {
        schema = schema.min(greaterThanOrEqualTo.value.value, {
          message: t('validation:atLeastCharacters', {
            fieldName,
            length: greaterThanOrEqualTo.value.value,
          }),
        });
      }

      const lessThanOrEqualTo = conditions.find(({ operator }) => operator === 'lessThanOrEquals');
      if (lessThanOrEqualTo && typeof lessThanOrEqualTo.value?.value === 'number') {
        schema = schema.max(lessThanOrEqualTo.value.value, {
          message: t('validation:atMostCharacters', {
            fieldName,
            length: lessThanOrEqualTo.value.value,
          }),
        });
      }

      const match = conditions.find(({ operator }) => operator === 'match');
      if (match && typeof match.value?.value === 'string') {
        schema = schema.regex(new RegExp(match.value.value), {
          message: t('validation:invalidFormat', { fieldName }),
        });
      }

      return schema;
    },
    [t],
  );

  const createNumberSchema = useCallback<
    Func<[fieldName: string, conditions: FieldMetadataConditionDescriptor[], messages: FieldValueErrorMessages, errorMap: z.ZodErrorMap], z.ZodTypeAny>
  >(
    (fieldName, conditions, _messages, errorMap) => {
      let schema = z.number({ errorMap });

      const greaterThanOrEqualTo = conditions.find(({ operator }) => operator === 'greaterThanOrEquals');
      if (greaterThanOrEqualTo && typeof greaterThanOrEqualTo.value?.value === 'number') {
        schema = schema.min(greaterThanOrEqualTo.value.value, {
          message: t('validation:greaterThanOrEqualTo', {
            fieldName,
            comparedFieldName: greaterThanOrEqualTo.value.value,
          }),
        });
      }

      const lessThanOrEqualTo = conditions.find(({ operator }) => operator === 'lessThanOrEquals');
      if (lessThanOrEqualTo && typeof lessThanOrEqualTo.value?.value === 'number') {
        schema = schema.max(lessThanOrEqualTo.value.value, {
          message: t('validation:lessThanOrEqualTo', {
            fieldName,
            comparedFieldName: lessThanOrEqualTo.value.value,
          }),
        });
      }

      return schema;
    },
    [t],
  );

  const createNullConditionSchema = useCallback<
    Func<[schema: z.ZodTypeAny, hasNotNullCondition: boolean, isDateTime: boolean, errorMap: z.ZodErrorMap], z.ZodTypeAny>
  >((schema, hasNotNullCondition, isDateTime, errorMap) => {
    if (hasNotNullCondition) {
      return schema;
    }

    if (isDateTime) {
      return z.nullable(z.union([schema, emptySchema.undefined(errorMap), emptySchema.null(errorMap), emptySchema.string(errorMap)], { errorMap }));
    }

    return z.nullable(schema);
  }, []);

  return useCallback<FieldValueSchemaCreator>(
    ({ fieldName, type, metadata }) => {
      const conditions = metadata?.condition;
      if (!conditions) {
        return z.any();
      }

      const hasNotNullCondition = !!conditions.find(({ operator }) => operator === 'notNull');
      const messages = createErrorMessage(fieldName);
      const errorMap = createErrorMap(hasNotNullCondition, messages);

      if (fieldLogic.isDataType.table(type)) {
        return createTableSchema(hasNotNullCondition, messages, errorMap);
      }

      let schema;
      let isDateTime = false;
      switch (type.name) {
        case 'list.text':
        case 'text': {
          schema = createTextSchema(fieldName, conditions, messages, errorMap);
          break;
        }

        case 'list.integer':
        case 'list.decimal':
        case 'list.currency':
        case 'integer':
        case 'decimal':
        case 'currency': {
          schema = createNumberSchema(fieldName, conditions, messages, errorMap);
          break;
        }

        case 'list.boolean':
        case 'boolean': {
          schema = z.boolean({ errorMap });
          break;
        }

        case 'list.datetime':
        case 'datetime': {
          isDateTime = true;
          schema = z.string({ errorMap }).datetime(messages.invalid_type_error);
          break;
        }

        case 'list.date':
        case 'date': {
          isDateTime = true;
          schema = z.string({ errorMap }).date(messages.invalid_type_error);
          break;
        }

        case 'list.time':
        case 'time': {
          isDateTime = true;
          schema = z.string({ errorMap }).time(messages.invalid_type_error);
          break;
        }

        default: {
          throw new ClientError({
            module: 'logic',
            message: 'Invalid field type',
            cause: type,
          });
        }
      }

      return createNullConditionSchema(schema, hasNotNullCondition, isDateTime, errorMap);
    },
    [createErrorMap, createErrorMessage, createNullConditionSchema, createNumberSchema, createTableSchema, createTextSchema],
  );
}

/**
 * Use Field ValidValue Schema Creator.
 *
 * @returns {FieldValidValueSchemaCreator} Field ValidValue Schema Creator.
 */
export function useFieldValidValueSchemaCreator(): FieldValidValueSchemaCreator {
  const { t } = useTranslation();

  const createErrorMap = useCallback<Func<[fieldName: string], z.ZodErrorMap>>(
    (fieldName) => (issue) => {
      if (issue.code === 'invalid_literal') {
        return {
          message: t(`validation:invalid`, { fieldName }),
        };
      }

      return {
        message: t('validation:invalidType', { fieldName }),
      };
    },
    [t],
  );

  return useCallback<FieldValidValueSchemaCreator>(
    ({ fieldName }) =>
      z.literal(true, {
        errorMap: createErrorMap(fieldName),
      }),
    [createErrorMap],
  );
}
